// client/src/pages/admin/AdminOrderInvoices.js
// Every invoice created by an online checkout, visible to all admins.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { AuthContext } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/currency';
import { downloadInvoice } from '../../utils/invoice';
import { API_BASE_URL } from '../../utils/media';

const paymentLabel = (order) => {
  if (order.status === 'Cancelled') return { label: 'Cancelled', color: 'default' };
  if (order.payment_status === 'paid') return { label: 'Paid', color: 'success' };
  return { label: 'Awaiting EFT', color: 'warning' };
};

const AdminOrderInvoices = () => {
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/admin/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Could not load online invoices.'))))
      .then(setOrders)
      .catch((err) => setError(err.message));
  }, [token]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!orders || !term) return orders || [];
    return orders.filter((o) => `${o.invoice_number} ${o.customer_email}`.toLowerCase().includes(term));
  }, [orders, search]);

  if (error) return <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>;
  if (!orders) return <CircularProgress size={28} sx={{ mb: 3 }} />;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Online order invoices</Typography>
          <Typography variant="body2" color="text.secondary">Created automatically at checkout.</Typography>
        </Box>
        {orders.length > 0 && (
          <TextField size="small" placeholder="Search by number or customer" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
        )}
      </Stack>
      {orders.length === 0 && <Typography variant="body2" color="text.secondary">No online orders yet.</Typography>}
      {orders.length > 0 && shown.length === 0 && <Typography variant="body2" color="text.secondary">Nothing matches that search.</Typography>}
      {shown.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell><TableCell>Customer</TableCell><TableCell>Date</TableCell>
                <TableCell align="right">Total</TableCell><TableCell>Status</TableCell><TableCell align="right">PDF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((order) => {
                const status = paymentLabel(order);
                return (
                  <TableRow key={order.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Link to={`/orders/${order.id}`}>{order.invoice_number}</Link></TableCell>
                    <TableCell>{order.customer_email}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                    <TableCell><Chip size="small" label={status.label} color={status.color} /></TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<DownloadOutlinedIcon />}
                        onClick={() => downloadInvoice({ orderId: order.id, invoiceNumber: order.invoice_number, token }).catch((err) => setError(err.message))}
                      >
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Divider sx={{ my: 4 }} />
    </Box>
  );
};

export default AdminOrderInvoices;
